import React, { useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';

import { Navbar } from "./Navbar";
import { Footer } from "./Footer";
import { CookieBanner } from "./CookieBanner";

interface LayoutProps {
  children: React.ReactNode;
  showNav?: boolean;
  showFooter?: boolean;
}

export function Layout({ children, showNav = true, showFooter = true }: LayoutProps) {
  const { t } = useTranslation();
  const progressBarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // If native CSS scroll animation is supported, GPU handles it at 60fps with 0 JS
    if (typeof CSS !== 'undefined' && CSS.supports && CSS.supports('animation-timeline', 'scroll()')) {
      return;
    }

    let ticking = false;
    const updateProgress = () => {
      if (!progressBarRef.current) return;
      const total = document.documentElement.scrollHeight - window.innerHeight;
      const progress = total > 0 ? window.scrollY / total : 0;
      progressBarRef.current.style.transform = `scaleX(${progress})`;
      ticking = false;
    };

    const onScroll = () => {
      if (!ticking) {
        requestAnimationFrame(updateProgress);
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-background text-foreground selection:bg-primary/30 overflow-x-hidden">
      {/* Skip to content — accessibility (WCAG 2.4.1) */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg focus:outline-none"
      >
        {t('misc_ui.skip_to_content')}
      </a>
      <div className="noise-overlay" aria-hidden="true" />
      <div ref={progressBarRef} className="scroll-progress-indicator" aria-hidden="true" />

      <div className="ambient-background" aria-hidden="true" />

      {/* Universal Navigation Header */}
      {showNav && (
        <>
          <Navbar />
          <div className="h-20" aria-hidden="true" />
        </>
      )}
      
      <main id="main-content" role="main" tabIndex={-1} className="relative z-10">
        {children}
      </main>

      {showFooter && <Footer />}
      <CookieBanner />
    </div>
  );
}
